import {objectFromEntries} from '@benchling/js-utils';
import React from 'react';

import CircularPolymerPreview from 'molbio/components/visualizations/CircularPolymerPreview';
import LinearPolymerPreview from 'molbio/components/visualizations/LinearPolymerPreview';
import {VisualPolymer, VisualPolymerFeature} from 'molbio/components/visualizations/PolymerPreviewConstants';
import {getFeatureLayers} from 'molbio/maps/MapsUtil';
import * as sequtil from 'molbio/SeqUtil';
import sortedBy from 'util/sortedBy';

const MINIMUM_ANNOTATION_PROPORTION = 0.1;
const MAXIMUM_FEATURE_LAYERS = 3;

/**
 * Gets no more than 3 layers of annotations, prioritizing the layers that contain the longest annotations.
 * Annotations that take up less than 10% of the full polymer length are filtered out.
 * Returns an array of layers, where a layer is an array of annotations to show in one ring.
 * Exported for testing.
 */
export function getAnnotationLayers(
  annotations: Array<VisualPolymerFeature>,
  seqLength: number
): Array<Array<VisualPolymerFeature>> {
  const minimumLength = MINIMUM_ANNOTATION_PROPORTION * seqLength;
  // Filter out annotations that are too small to show and add a CID which is needed for getFeatureLayers.
  const bigAnnotations = annotations
    .filter(({start, end}) => sequtil.length(start, end, seqLength) >= minimumLength)
    .map((anno, idx) => ({...anno, cid: String(idx)}));
  const cidsToLength = objectFromEntries(
    bigAnnotations.map((anno) => [anno.cid, sequtil.length(anno.start, anno.end, seqLength)])
  );
  const sortedAnnotations = sortedBy(bigAnnotations, [(anno) => -cidsToLength[anno.cid]]);
  const {max, layers} = getFeatureLayers(
    sortedAnnotations,
    (anno) => ({model: anno, pos: anno.start}),
    (anno) => ({model: anno, pos: anno.end}),
    true
  );
  const annotationsInLayers: Array<Array<VisualPolymerFeature & {cid: string}>> = Array.from(
    {length: max + 1},
    () => []
  );
  for (const {model, layer} of layers) {
    annotationsInLayers[layer].push(model);
  }
  if (max < MAXIMUM_FEATURE_LAYERS) {
    return annotationsInLayers;
  }

  // If there are too many layers, only include the 3 layers that contain the longest annotations.
  // Currently the only polymer features we show are annotations, but we will later include
  // other features in those 3 layers as well.
  const sortedLayers = sortedBy(annotationsInLayers, [
    (layer) => -Math.max(...layer.map((anno) => cidsToLength[anno.cid])),
  ]);
  return sortedLayers.slice(0, MAXIMUM_FEATURE_LAYERS);
}

export interface PolymerPreviewProps {
  polymer: VisualPolymer;
  uniqueIdPrefix: string;
}

/**
 * A static, non-interactive preview of a polymer that may show visuals of relevant information
 * for changes made to the polymer.
 */
export default function PolymerPreview({polymer, uniqueIdPrefix}: PolymerPreviewProps): JSX.Element {
  const annotationLayers = getAnnotationLayers(polymer.annotations, polymer.length);
  if (polymer.circular) {
    return (
      <CircularPolymerPreview
        polymer={polymer}
        annotationLayers={annotationLayers}
        uniqueIdPrefix={uniqueIdPrefix}
      />
    );
  } else {
    return <LinearPolymerPreview polymer={polymer} annotationLayers={annotationLayers} />;
  }
}
